import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import {
    MdLocalFireDepartment,
    MdListAlt,
    MdCalendarToday,
    MdExpandMore,
    MdRestaurant,
} from "react-icons/md";
import {
    GiMuscleUp,
    GiWheat,
    GiButter,
    GiMeal
} from "react-icons/gi";
import ReactGA from "react-ga4";

import isProd from '../config';

const API_URL = isProd
    ? process.env.REACT_APP_API_URL_LIVE
    : process.env.REACT_APP_API_URL_TEST;


function CollapsibleSection({ title, icon, children, open, onClick, id, labelId }) {
    const contentRef = useRef(null);
    const [height, setHeight] = useState(open ? "auto" : "0px");

    useEffect(() => {
        if (open) {
            const scrollHeight = contentRef.current?.scrollHeight || 0;
            setHeight(scrollHeight + "px");
            const timeout = setTimeout(() => setHeight("auto"), 300);
            return () => clearTimeout(timeout);
        } else {
            if (contentRef.current) {
                setHeight(contentRef.current.scrollHeight + "px");
                setTimeout(() => setHeight("0px"), 10);
            }
        }
    }, [open, children]);

    return (
        <section
            className="bg-white rounded-xl shadow-lg mb-6 border-2 border-gray-200 hover:shadow-xl transition-shadow duration-200"
            aria-labelledby={labelId}
        >
            <button
                className="w-full flex items-center justify-between px-6 py-5 focus:outline-none text-xl md:text-2xl font-bold text-green-800 bg-green-50 hover:bg-green-100 rounded-t-xl border-none cursor-pointer transition-colors duration-200 min-h-[48px] focus:ring-4 focus:ring-yellow-300"
                onClick={onClick}
                aria-expanded={open}
                aria-controls={id}
                id={labelId}
                type="button"
            >
                <span className="flex items-center gap-3">
                    {icon}
                    {title}
                </span>
                <MdExpandMore
                    size={48}
                    className={`transition-transform duration-300 ${open ? "rotate-180" : ""} text-green-600`}
                />
            </button>
            <div
                id={id}
                ref={contentRef}
                className="overflow-hidden transition-all duration-300 bg-green-50"
                style={{ maxHeight: height }}
            >
                <div className="px-6 py-4 text-lg md:text-xl leading-relaxed">
                    {children}
                </div>
            </div>
        </section>
    );
}

const mealOrder = [
    "breakfast",
    "lunch",
    "dinner",
    "bedtime_snack",
    "snack",
    "snack_1",
    "snack1",
    "snack_2",
    "snack2",
];

function formatGroceryItem(item) {
    if (!item) return "";
    const qty = item.quantity !== undefined ? item.quantity : "";
    const unit = item.unit ? item.unit : "";
    const ingredient = item.ingredient ? item.ingredient : "";
    return `${qty} ${unit} ${ingredient}`.replace(/\s+/g, " ").trim();
}

export default function SuccessPage() {
    const { planId } = useParams();
    const cleanPlanId = planId.split('&')[0];
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    const [plan, setPlan] = useState(null);
    const [error, setError] = useState("");
    const [groceryOpen, setGroceryOpen] = useState(false);
    const [openDays, setOpenDays] = useState([]);
    const [recommendationsOpen, setRecommendationsOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const alertedRef = useRef(false);

    useEffect(() => {

        ReactGA.event('PURCHASE', {
            transaction_id: sessionId,
            value: 9.99,
            currency: 'USD',
            items: [
                {
                    item_id: 'monthly_meal_plan',
                    item_name: 'Monthly Meal Plan',
                    price: 9.99,
                    quantity: 1,
                    item_category: 'Subscription',
                    plan_id: cleanPlanId
                }
            ],
            plan_id: cleanPlanId,
            subscription_type: 'monthly',
            plan_name: 'Monthly Meal Plan'
        });

        // window.dataLayer = window.dataLayer || [];
        // window.dataLayer.push({
        //     event: 'PURCHASE',
        //     value: 9.99,
        //     currency: 'USD',
        //     items: [
        //         {
        //             item_id: 'monthly_meal_plan',
        //             item_name: 'Monthly Meal Plan',
        //             price: 9.99,
        //             quantity: 1,
        //             item_category: 'Subscription',
        //             plan_id: cleanPlanId,
        //         }
        //     ],
        //     plan_id: cleanPlanId,
        //     subscription_type: 'monthly',
        //     plan_name: 'Monthly Meal Plan'
        // });
    }, [cleanPlanId, sessionId]);


    // const [generatingImages, setGeneratingImages] = useState(false);
    // async function generateImagesForPlan() {
    //     setGeneratingImages(true);
    //     try {
    //         console.log(`Generating images for plan ID: ${cleanPlanId}`);
    //         const res = await fetch(`https://addimagestomeal-6w2wugyllq-uc.a.run.app`, {
    //             method: 'POST',
    //             headers: { 'Content-Type': 'application/json' },
    //             body: JSON.stringify({ planId: cleanPlanId }), 
    //         });
    //         if (!res.ok) throw new Error('Failed to generate images.');
    //         setTimeout(() => {
    //             window.location.reload();
    //         }, 3000);
    //     } catch (err) {
    //         alert(err.message || 'Image generation failed.');
    //     } finally {
    //         setGeneratingImages(false);
    //     }
    // }

    function removeCitationBrackets(text) {
        if (!text || typeof text !== "string") return text;
        return text.replace(/\[\d+\]/g, "");
    }

    function sortMealsByType(mealsObj) {
        const mealsArr = Object.entries(mealsObj).map(([key, meal]) => ({
            ...meal,
            _mealKey: key
        }));
        return mealsArr.sort((a, b) => {
            const typeA = (a.meal_type || a._mealKey || "").toLowerCase();
            const typeB = (b.meal_type || b._mealKey || "").toLowerCase();
            const idxA = mealOrder.indexOf(typeA);
            const idxB = mealOrder.indexOf(typeB);
            return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
        });
    }

    function getNextPlanDate(createdAt) {
        if (!createdAt) return null;
        const created = new Date(createdAt);
        const next = new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000);
        return next.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }

    useEffect(() => {
        setLoading(true);

        fetch(`${API_URL}/?planId=${planId}`, {
            method: 'POST'
        })
            .then(res => {
                if (!res.ok) throw new Error("Plan not found.");
                return res.json();
            })
            .then(data => {
                setPlan(data);
                if (data?.meal_plan?.days?.length > 0) setOpenDays([0]);
                if (data?.meal_plan?.days?.length === 1 && !alertedRef.current) {
                    // alert("Note: Your current meal plan covers only 1 day. For a more comprehensive plan, consider generating a meal plan for multiple days.");
                    alertedRef.current = true;
                }
            })
            .catch(err => setError(err.message || "Error loading plan."))
            .finally(() => setLoading(false));
    }, [planId]);

    const recommendationsArr = plan?.recommendations?.recommendations || [];
    const days = plan?.meal_plan?.days || [];
    const mealsPerDay = plan?.recommendations?.meals_per_day;
    const calorieTarget = plan?.recommendations?.calorie_target;
    const recommendedIngredients = plan?.recommendations?.recommended_ingredients || [];
    const avoidIngredients = plan?.recommendations?.avoid_ingredients || [];
    const nextPlanDate = getNextPlanDate(plan?.created_at);
    // const images = plan?.images;MdHelp

    // useEffect(() => {
    //     if (images === false && !generatingImages) {
    //         // generateImagesForPlan();
    //     }
    //     // eslint-disable-next-line
    // }, [images, planId]);

    const toggleDay = idx =>
        setOpenDays(openDays =>
            openDays.includes(idx)
                ? openDays.filter(i => i !== idx)
                : [...openDays, idx]
        );

    if (loading)
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-600 border-t-transparent mx-auto mb-4"></div>
                    <span className="text-2xl md:text-3xl text-green-700 font-bold">Creating your personalized meal plan...</span>
                </div>
            </div>
        );

    if (error)
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 px-4">
                <div className="text-center max-w-md">
                    <span className="text-xl md:text-2xl font-semibold text-red-700 mb-4 block">
                        We're sorry, but we couldn't load your meal plan. Please try again or contact support.
                    </span>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg text-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-yellow-300 min-h-[48px]"
                        aria-label="Try loading the meal plan again"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );

    if (!plan) return null;

    return (
        <div className="min-h-screen bg-amber-50 flex flex-col items-center py-4 px-4">
            <style>{`
                @media print {
                    button[aria-label="Print meal plan as PDF"] {
                        display: none !important;
                    }
                    .shadow, .shadow-lg, .shadow-xl {
                        box-shadow: none !important;
                    }
                    body, .bg-amber-50 {
                        background: #fff !important;
                    }
                    [style*="max-height"] {
                        max-height: none !important;
                        overflow: visible !important;
                    }
                }
            `}</style>

            <header className="w-full max-w-4xl text-center mb-8">
                <div className="flex items-center justify-center gap-4 mb-4">
                    <img src="/logo.svg" alt="Logo" width={40} height={40} />
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-green-800 leading-tight">
                        Your Meal Plan
                    </h1>
                </div>
            </header>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl shadow-xl p-8 max-w-3xl mx-auto mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                <div className="text-center mb-8">
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        You now have full access to your tailored meal plans and ongoing nutrition support.
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6 mb-4">
                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                        <div className="text-center">
                            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl">🍽️</span>
                            </div>
                            <h3 className="font-bold text-base mb-1">Weekly Meal Plans</h3>
                            <p className="text-sm text-gray-600">
                                {nextPlanDate && (
                                    <span>
                                        New plan will be generated:<br /> {nextPlanDate}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                        <div className="text-center">
                            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl">🔔</span>
                            </div>
                            <h3 className="font-bold text-base mb-1">Personal Updates</h3>
                            <p className="text-sm text-gray-600">
                                Receive monthly plan updates based on your changing needs.
                            </p>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                        <div className="text-center">
                            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl">💡</span>
                            </div>
                            <h3 className="font-bold text-base mb-1">Ongoing Support</h3>
                            <p className="text-sm text-gray-600">
                                Your plan adapts as your treatment and health evolve.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <main className="w-full max-w-4xl" role="main">
                <CollapsibleSection
                    title={
                        <div className="flex items-center gap-2">
                            Nutrition Recommendations
                        </div>
                    }
                    icon={<MdListAlt size={36} aria-label="Recommendations" />}
                    open={recommendationsOpen}
                    onClick={() => setRecommendationsOpen(open => !open)}
                    id="recommendations-section"
                    labelId="recommendations-label"
                >
                    {(mealsPerDay || calorieTarget) && (
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
                            <h3 className="font-bold text-xl mb-4 text-blue-800">Your Daily Targets:</h3>
                            <div className="flex flex-wrap gap-6">
                                {mealsPerDay && (
                                    <div className="flex items-center gap-2 text-lg">
                                        <MdRestaurant size={28} className="text-blue-600" />
                                        <span><strong>Meals per day:</strong> {mealsPerDay}</span>
                                    </div>
                                )}
                                {calorieTarget && (
                                    <div className="flex items-center gap-2 text-lg">
                                        <MdLocalFireDepartment size={28} className="text-orange-500" />
                                        <span><strong>Daily calories:</strong> {calorieTarget}</span>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 p-3 bg-white rounded border border-blue-300">
                                <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> Our meal plans follow evidence-based guidelines recommending
                                    <strong> 5-6 eating occasions daily</strong> (3 main meals + 2-3 snacks) spaced every 2-3 hours
                                    for optimal nutrition during cancer treatment.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-6 relative">
                        {recommendationsArr.map((rec, idx) => (
                            <div
                                key={idx}
                                className="border-l-4 border-green-400 pl-6 py-2"
                            >
                                <h4 className="font-bold text-xl mb-3 text-green-800">{rec.recommendation}</h4>
                                <p className="text-gray-700 text-lg leading-relaxed">{removeCitationBrackets(rec.rationale)}</p>
                            </div>
                        ))}
                    </div>

                    {recommendedIngredients.length > 0 && (
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mt-6 relative">
                            <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-green-800">
                                <GiWheat size={28} />
                                Recommended Ingredients:
                            </h3>
                            <ul className="space-y-3">
                                {recommendedIngredients.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-green-600 mt-1 text-xl">•</span>
                                        <div className="text-lg">
                                            <strong className="text-green-800">{item.ingredient}</strong>
                                            {item.rationale && (
                                                <span className="text-gray-700 ml-2 block mt-1">— {item.rationale}</span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {avoidIngredients.length > 0 && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mt-6 relative">
                            <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-red-800">
                                <MdListAlt size={28} />
                                Ingredients to Avoid:
                            </h3>
                            <ul className="space-y-3">
                                {avoidIngredients.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-red-600 mt-1 text-xl">•</span>
                                        <div className="text-lg">
                                            <strong className="text-red-800">{item.ingredient}</strong>
                                            {item.rationale && (
                                                <span className="text-red-700 ml-2 block mt-1">— {item.rationale}</span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CollapsibleSection>

                {days.map((day, dayIdx) => (
                    <CollapsibleSection
                        key={day.day}
                        title={day.day}
                        icon={<MdCalendarToday size={36} aria-label="Day" />}
                        open={openDays.includes(dayIdx)}
                        onClick={() => toggleDay(dayIdx)}
                        id={`day-section-${dayIdx}`}
                        labelId={`day-label-${dayIdx}`}
                    >
                        <div className="space-y-8">
                            {sortMealsByType(day.meals).map((meal, mealIdx) => {
                                const hasImage = !!meal.imageUrl;
                                return (
                                    <article
                                        key={mealIdx}
                                        className="bg-white rounded-xl shadow-lg p-0 border-2 border-gray-100 hover:shadow-xl transition-shadow duration-200 overflow-hidden"
                                        aria-label={`${meal.meal_type} - ${meal.name}`}
                                    >
                                        <div className={`flex flex-col${hasImage ? " md:flex-row" : ""}`}>
                                            <div className={`w-full${hasImage ? " md:w-1/2" : ""} p-6 flex flex-col justify-between`}>
                                                <div>
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <GiMeal size={28} className="text-green-700" aria-label="Meal" />
                                                        <h3 className="text-xl font-bold text-green-800">
                                                            {meal.meal_type
                                                                ? meal.meal_type.charAt(0).toUpperCase() + meal.meal_type.slice(1)
                                                                : "Meal"}
                                                            <span className="text-green-600 font-normal"> • </span>
                                                            <span className="text-gray-700">{meal.name}</span>
                                                        </h3>
                                                    </div>
                                                    {hasImage && (
                                                        <div className="block md:hidden w-full flex justify-center mb-4">
                                                            <div className="w-full h-48">
                                                                <img
                                                                    src={meal.imageUrl}
                                                                    alt={meal.name || "Meal image"}
                                                                    className="w-full h-full object-cover rounded-lg shadow-sm"
                                                                    onError={e => (e.target.style.display = 'none')}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap gap-4 text-md text-gray-600 mb-4">
                                                        {meal.preparation_time !== undefined && (
                                                            <span><strong>Prep:</strong> {meal.preparation_time} min</span>
                                                        )}
                                                        {meal.cook_time !== undefined && (
                                                            <span><strong>Cook:</strong> {meal.cook_time} min</span>
                                                        )}
                                                        <span>
                                                            <strong>Total:</strong>{" "}
                                                            {meal.total_time !== undefined
                                                                ? `${meal.total_time} min`
                                                                : `${(meal.preparation_time || 0) + (meal.cook_time || 0)} min`}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-6">
                                                        <div className="flex items-center gap-2">
                                                            <MdLocalFireDepartment size={20} className="text-orange-500" />
                                                            <span className="text-sm"><strong className="text-orange-600">Calories:</strong> {meal.calories}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <GiMuscleUp size={20} className="text-blue-600" />
                                                            <span className="text-sm"><strong className="text-blue-600">Protein:</strong> {meal.nutrition?.protein}g</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <GiWheat size={20} className="text-green-600" />
                                                            <span className="text-sm"><strong className="text-green-600">Carbs:</strong> {meal.nutrition?.carbs}g</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <GiButter size={20} className="text-yellow-600" />
                                                            <span className="text-sm"><strong className="text-yellow-600">Fat:</strong> {meal.nutrition?.fat}g</span>
                                                        </div>
                                                    </div>
                                                    <div className="mb-6">
                                                        <h4 className="font-semibold text-lg mb-3 text-gray-800">Ingredients:</h4>
                                                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                                            {Array.isArray(meal.ingredients) &&
                                                                meal.ingredients.map((ing, i) => (
                                                                    <li key={i} className="flex items-start gap-2 text-sm">
                                                                        <span className="text-green-600 mt-1 text-xs">•</span>
                                                                        <span className="text-gray-700">{ing}</span>
                                                                    </li>
                                                                ))}
                                                        </ul>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-lg mb-3 text-gray-800">Instructions:</h4>
                                                        <ol className="space-y-2 text-md">
                                                            {(meal.preparation || "")
                                                                .split(/(?=Step \d+:)/g)
                                                                .filter(Boolean)
                                                                .map((step, i, arr) => {
                                                                    const isLastNote = i === arr.length - 1 && !/^Step \d+:/.test(step.trim());
                                                                    return (
                                                                        <li key={i} className={`flex gap-3 leading-relaxed ${isLastNote ? "italic font-medium text-green-700" : "text-gray-700"}`}>
                                                                            {!isLastNote && (
                                                                                <span className="bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                                                                                    {i + 1}
                                                                                </span>
                                                                            )}
                                                                            <span className={!isLastNote ? "" : "ml-9"}>
                                                                                {step.trim().replace(/^Step \d+:\s*/, "")}
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                })}
                                                        </ol>
                                                    </div>
                                                </div>
                                            </div>
                                            {hasImage && (
                                                <div className="hidden md:flex w-full md:w-1/2 items-center justify-center bg-gray-50">
                                                    <div className="w-full h-full flex items-center justify-center p-8">
                                                        <img
                                                            src={meal.imageUrl}
                                                            alt={meal.name || "Meal image"}
                                                            className="w-full h-[20rem] max-h-[28rem] object-cover rounded-lg shadow-md"
                                                            onError={e => (e.target.style.display = 'none')}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    </CollapsibleSection>
                ))}

                {Array.isArray(plan.grocery_list) && plan.grocery_list.length > 0 && (
                    <CollapsibleSection
                        title="Shopping List"
                        icon={<MdListAlt size={36} aria-label="Shopping List" />}
                        open={groceryOpen}
                        onClick={() => setGroceryOpen(open => !open)}
                        id="grocery-list"
                        labelId="grocery-list-label"
                    >
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                            <h3 className="font-bold text-xl mb-4 text-blue-800">Your Shopping List:</h3>
                            <ul className="space-y-3">
                                {plan.grocery_list.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-blue-600 mt-1 text-xl">•</span>
                                        <span className="text-lg text-gray-700">{formatGroceryItem(item)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </CollapsibleSection>
                )}

                {plan.recommendations_user && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mt-6">
                        <h3 className="font-bold text-xl mb-4 text-green-800">Personal Notes for You:</h3>
                        <div className="whitespace-pre-line text-lg text-gray-700 leading-relaxed">
                            {plan.recommendations_user}
                        </div>
                    </div>
                )}

                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mt-8" role="alert">
                    <h3 className="font-bold text-xl mb-4 text-red-800">Important Health Notice:</h3>
                    <p className="text-lg text-red-700 leading-relaxed">
                        This site does not offer medical advice. Always prioritize your health and safety, and consult a healthcare professional with any health concerns. This program has not been evaluated by the FDA and is not intended to diagnose, treat, cure, or prevent any disease.
                    </p>
                </div>
            </main>

            <footer className="text-center mt-12 pb-8 px-4 mb-10">
                <p className="text-lg text-green-700 mb-2">Made with care for your health and wellness ❤️</p>
                <p className="text-gray-600">
                    <strong>Need Help?</strong> contact us at{" "}
                    <a href="mailto:support@oncodiary.com" className="text-green-600 hover:text-green-700 font-bold">
                        support@oncodiary.com
                    </a>
                </p>
                <div className="mt-4 space-x-4">
                    <Link to="/terms" className="underline hover:text-[#4B8F5A] transition">Terms of Use</Link>
                    <span className="text-gray-400">|</span>
                    <Link to="/privacy" className="underline hover:text-[#4B8F5A] transition">Privacy Policy</Link>
                </div>
            </footer>
        </div>
    );
}
